import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { getManagedEvents } from "@/features/supply-config/managed-events-api";
import {
  SupplyListCard,
  SupplyPageHeader,
} from "@/features/supply-config/ui/supply-list";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

export default function ManagedEvents() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const { data: items = [], isLoading, error } = useQuery({
    queryKey: ["managed-events"],
    queryFn: getManagedEvents,
  });

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(
      (m) =>
        m.name.toLowerCase().includes(s) ||
        (m.teams ?? []).some((t) => t.name.toLowerCase().includes(s)) ||
        m.category.toLowerCase().includes(s),
    );
  }, [items, q]);

  return (
    <div className="p-6 space-y-4">
      <SupplyPageHeader
        title="Managed Events"
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("calendly-data")}>
              Calendly data
            </Button>
            <HasAccess permission="write">
              <Button className="bg-sidebar" onClick={() => navigate("new")}>
                <Plus className="h-4 w-4 mr-1" /> New Managed Event
              </Button>
            </HasAccess>
          </>
        }
      />

      <SupplyListCard
        isLoading={isLoading}
        error={error}
        isEmpty={filtered.length === 0}
        emptyText={
          q ? "No managed events match your search." : "No managed events yet."
        }
        controls={
          <>
            <span className="text-xs text-gray-400">{filtered.length}</span>
            <div className="relative w-72 max-w-full">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, team, category…"
                className="h-8 pl-7 text-sm"
              />
            </div>
          </>
        }
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white text-left text-xs text-gray-500">
              <th className="py-2 px-4">Name</th>
              <th className="py-2 px-4">Category</th>
              <th className="py-2 px-4">Teams</th>
              <th className="py-2 px-4">Attendees</th>
              <th className="py-2 px-4">Duration</th>
              <th className="py-2 px-4">Round-robin</th>
              <th className="py-2 px-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr
                key={m.id}
                className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(m.id)}
              >
                <td className="py-2 px-4 font-medium">{m.name}</td>
                <td className="py-2 px-4">
                  <Badge variant="outline">{m.category}</Badge>
                </td>
                <td className="py-2 px-4">
                  {m.teams?.length
                    ? m.teams.map((t) => t.name).join(", ")
                    : "—"}
                </td>
                <td className="py-2 px-4">{m.participant_scope}</td>
                <td className="py-2 px-4">
                  {m.duration_minutes ? `${m.duration_minutes}m` : "—"}
                </td>
                <td className="py-2 px-4">
                  {m.round_robin_pool_active ? "Yes" : "No"}
                </td>
                <td className="py-2 px-4">
                  <Badge variant={m.status === "ACTIVE" ? "active" : "inactive"}>
                    {m.status}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SupplyListCard>
    </div>
  );
}
